import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ContractDeleteModal from "./ContractDeleteModal";

const ContractRow = ({
  contract,
  contractList,
  setContractList,
  allContracts,
  setAllContracts,
  openEditModal,
  token,
  API,
  onDeleted,
}) => {
  const { t } = useTranslation();
  const [modalDelete, setModalDelete] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const actionsButtonRef = useRef(null);

  const contractReference = contract.contract_number || "—";

  const updateMenuPosition = () => {
    if (actionsButtonRef.current) {
      const rect = actionsButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 120,
      });
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        actionsButtonRef.current &&
        !actionsButtonRef.current.contains(e.target) &&
        !e.target.closest(".table-standard-row-actions-menu-portal")
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleCopyEmail = async (event) => {
    event.stopPropagation();
    const email = contract.collectorEmail || "";
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      toast.success(t("settingsContacts.emailCopied"), {
        className: "custom-toast",
      });
    } catch {
      toast.error(t("settingsContacts.copyError"));
    }
  };

  const handleDeleted = () => {
    setContractList(contractList.filter((c) => c._id !== contract._id));
    if (setAllContracts && allContracts) {
      setAllContracts(allContracts.filter((c) => c._id !== contract._id));
    }
    onDeleted?.();
  };

  return (
    <>
      <article className="table-standard-row">
        <div className="table-standard-row-cell" title={contractReference}>
          <span className="table-standard-row-cell-truncate text-sm font-semibold text-[#222222]">
            {contractReference || "—"}
          </span>
        </div>
        <div
          className="table-standard-row-cell"
          title={contract.collector || ""}
        >
          <span className="table-standard-row-cell-truncate text-sm text-[#5e5e5e]">
            {contract.collector || "—"}
          </span>
        </div>
        <div
          className="table-standard-row-cell"
          title={contract.collectorEmail || ""}
        >
          <div className="flex items-center gap-2 min-w-0 w-full">
            <span className="table-standard-row-cell-truncate text-sm text-[#5e5e5e] min-w-0">
              {contract.collectorEmail || "—"}
            </span>
            {contract.collectorEmail ? (
              <button
                type="button"
                className="shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-md border border-[#e0e0e0] bg-white text-[#5e5e5e] hover:bg-[#f5f5f5]"
                onClick={handleCopyEmail}
                aria-label={t("settingsContacts.copyEmail")}
              >
                <i className="fas fa-copy text-xs" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
        <article className="table-standard-row-cell table-standard-row-actions">
          <button
            ref={actionsButtonRef}
            type="button"
            className="table-standard-row-actions-dots"
            onClick={(event) => {
              event.stopPropagation();
              if (!isOpen && actionsButtonRef.current) {
                const rect = actionsButtonRef.current.getBoundingClientRect();
                setMenuPosition({
                  top: rect.bottom + 4,
                  left: rect.right - 120,
                });
              }
              setIsOpen(!isOpen);
            }}
            aria-label={t("Actions")}
            aria-expanded={isOpen}
          >
            <i
              className={isOpen ? "fas fa-times" : "fas fa-ellipsis-v"}
              aria-hidden
            />
          </button>
          {isOpen &&
            createPortal(
              <div
                className="table-standard-row-actions-menu-portal action-choice display-action"
                style={{
                  position: "fixed",
                  top: menuPosition.top,
                  left: menuPosition.left,
                  zIndex: 9999,
                  width: "auto",
                  minWidth: 120,
                  maxWidth: 160,
                }}
              >
                <button
                  type="button"
                  className="action"
                  onClick={(event) => {
                    event.stopPropagation();
                    openEditModal(contract);
                    setIsOpen(false);
                  }}
                >
                  {t("Edit")}
                </button>
                <button
                  type="button"
                  className="action"
                  onClick={(event) => {
                    event.stopPropagation();
                    setModalDelete(true);
                    setIsOpen(false);
                  }}
                >
                  {t("Delete")}
                </button>
              </div>,
              document.body,
            )}
        </article>
      </article>

      {modalDelete ? (
        <ContractDeleteModal
          isOpen={modalDelete}
          onClose={() => setModalDelete(false)}
          contract={contract}
          token={token}
          API={API}
          onDeleted={handleDeleted}
        />
      ) : null}
    </>
  );
};

export default ContractRow;
